import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';
import { supabase } from '../../lib/supabase';
import type { SocialVideo } from '../admin/components/SocialTab';

const getEmbedUrl = (plataforma: 'tiktok' | 'instagram', url: string): string => {
  if (plataforma === 'tiktok') {
    const match = url.match(/\/video\/(\d+)/);
    if (match) return `https://www.tiktok.com/embed/v2/${match[1]}`;
    return '';
  }
  if (plataforma === 'instagram') {
    const match = url.match(/\/(reel|p)\/([A-Za-z0-9_-]+)/);
    if (match) return `https://www.instagram.com/p/${match[2]}/embed/`;
    return '';
  }
  return '';
};

const platformBadge = {
  tiktok:    { icon: 'ri-tiktok-line',    label: 'TikTok',    cls: 'bg-zinc-900 text-white border-zinc-700' },
  instagram: { icon: 'ri-instagram-line', label: 'Instagram', cls: 'bg-pink-500/10 text-pink-400 border-pink-500/30' },
};

const VideoCard = ({ video }: { video: SocialVideo }) => {
  const embedUrl = getEmbedUrl(video.plataforma, video.url);
  const badge = platformBadge[video.plataforma];
  const isVertical = video.plataforma === 'tiktok';

  return (
    <div className="bg-zinc-900 border border-zinc-800 overflow-hidden flex flex-col">
      {/* Embed directo */}
      <div className="w-full overflow-hidden" style={{ height: isVertical ? 560 : 400 }}>
        {embedUrl ? (
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allow="encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            title={video.titulo}
            scrolling="no"
          />
        ) : (
          <div className="h-full flex items-center justify-center bg-zinc-950 text-zinc-600 text-sm">
            Enlace no válido
          </div>
        )}
      </div>

      {/* Footer: título + botón ir al vídeo */}
      <a
        href={video.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-4 py-3 border-t border-zinc-800 hover:bg-zinc-800/60 transition-colors cursor-pointer group"
      >
        <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded border ${badge.cls}`}>
          <i className={`${badge.icon} text-xs`} />
        </div>
        <p className="text-zinc-300 text-sm font-medium flex-1 min-w-0 truncate group-hover:text-white transition-colors">
          {video.titulo}
        </p>
        <div className="w-6 h-6 flex items-center justify-center text-zinc-500 group-hover:text-yellow-400 transition-colors flex-shrink-0">
          <i className="ri-external-link-line text-sm" />
        </div>
      </a>
    </div>
  );
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const socialJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Social – Taller Mecánico Kevin Alberto Ronda',
    url: `${siteUrl}/social`,
    description:
      'Sigue el día a día del Taller Mecánico Kevin Alberto en TikTok e Instagram. Vídeos de mecánica, trucos y novedades del taller en Ronda, Málaga.',
    inLanguage: 'es',
    isPartOf: { '@type': 'WebSite', url: siteUrl },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Taller Mecánico Kevin Alberto',
    url: siteUrl,
    sameAs: [
      'https://www.facebook.com/taller.mecanico.Kevin',
      'https://www.tiktok.com/@taller.mecanico.kevin',
      'https://www.instagram.com/taller.mecanico.kevin',
    ],
  },
];

const SocialPage = () => {
  const [videos, setVideos] = useState<SocialVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'tiktok' | 'instagram'>('all');

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('value')
        .eq('key', 'social_videos')
        .maybeSingle();
      if (data?.value) setVideos(data.value as SocialVideo[]);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = filter === 'all' ? videos : videos.filter((v) => v.plataforma === filter);
  const hasTikTok = videos.some((v) => v.plataforma === 'tiktok');
  const hasInstagram = videos.some((v) => v.plataforma === 'instagram');

  return (
    <div className="flex flex-col min-h-screen bg-black">
      <SEO
        title="Social – Taller Mecánico Kevin Alberto Ronda"
        description="Sigue el día a día del taller en TikTok e Instagram. Vídeos de mecánica, trucos y novedades del Taller Kevin Alberto en Ronda, Málaga."
        keywords="taller mecánico Ronda redes sociales, TikTok mecánico, Instagram taller Ronda"
        canonical="/social"
        jsonLd={socialJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-zinc-950 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-0.5 bg-yellow-400" />
            <span className="text-yellow-400 text-xs font-black uppercase tracking-widest">Síguenos</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight">
                Nuestro contenido<br />
                <span className="text-yellow-400">en redes sociales</span>
              </h1>
              <p className="text-zinc-400 text-sm mt-4 max-w-lg leading-relaxed">
                Mira cómo trabajamos, trucos de mecánica y el día a día del taller.
                ¡Síguenos para no perderte nada!
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {hasTikTok && (
                <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white text-xs font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors">
                  <i className="ri-tiktok-line text-sm" /> TikTok
                </a>
              )}
              {hasInstagram && (
                <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/30 text-pink-400 text-xs font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors">
                  <i className="ri-instagram-line text-sm" /> Instagram
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <main className="flex-grow py-12">
        <div className="max-w-6xl mx-auto px-4 md:px-8">

          {(hasTikTok && hasInstagram) && (
            <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 p-1 mb-8 w-fit">
              {([
                { id: 'all',       label: 'Todo',      icon: 'ri-apps-line' },
                { id: 'tiktok',    label: 'TikTok',    icon: 'ri-tiktok-line' },
                { id: 'instagram', label: 'Instagram', icon: 'ri-instagram-line' },
              ] as const).map((f) => (
                <button key={f.id} onClick={() => setFilter(f.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-wide cursor-pointer whitespace-nowrap transition-all ${
                    filter === f.id ? 'bg-yellow-400 text-black' : 'text-zinc-400 hover:text-white'
                  }`}>
                  <i className={f.icon} /> {f.label}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-32">
              <i className="ri-loader-4-line animate-spin text-yellow-400 text-4xl" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 border border-dashed border-zinc-800 gap-5">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-zinc-900 border border-zinc-800 rounded-full">
                  <i className="ri-tiktok-line text-zinc-500 text-2xl" />
                </div>
                <div className="w-16 h-16 flex items-center justify-center bg-zinc-900 border border-zinc-800 rounded-full">
                  <i className="ri-instagram-line text-zinc-500 text-2xl" />
                </div>
              </div>
              <p className="text-zinc-400 text-base font-semibold">Próximamente contenido en redes sociales</p>
              <p className="text-zinc-600 text-sm">¡Síguenos para no perderte nada del taller!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SocialPage;
